<script setup lang="ts">
import { onMounted, onUnmounted, ref, reactive, computed, watch, type Component } from 'vue'
import L from 'leaflet'
import 'leaflet-rotate'
import type { Map as LeafletMap } from 'leaflet'
import {
  CRANE_TYPES, makeCraneIcon, makeLandmarkIcon,
  styleBoundary, styleBlock, styleArea, styleRoad, styleBuilding,
  popupBlock, popupArea, popupRoad, popupBuilding, popupLandmark,
} from '@/layers/dvp-layers'
import { useVehicleTracking } from '@/composables/useVehicleTracking'
import { useMockGps } from '@/composables/useMockGps'
import { useSlotGrid } from '@/composables/useSlotGrid'
import { useContainerStream } from '@/composables/useContainerStream'
import { useContainerSearch } from '@/composables/useContainerSearch'
import { SlotGridLayer } from '@/layers/SlotGridLayer'
import type { ColorMode } from '@/layers/SlotGridLayer'
import { RtgCraneLayer } from '@/layers/RtgCraneLayer'
import { useVesselLayer } from '@/composables/useVesselLayer'
import type { VesselFilter } from '@/composables/useVesselLayer'
import type { SelectedObject } from '@/types/selection'
import VesselDetailPanel from '@/components/detail-panels/VesselDetailPanel.vue'

const panelMap: Record<SelectedObject['type'], Component> = {
  vessel: VesselDetailPanel,
}

// ─── Tab navigation ───────────────────────────────────────────────────────────
type Tab = 'overview' | 'berth' | 'yard' | 'gate' | 'report'
const activeTab = ref<Tab>('overview')
const tabs: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Giám sát Toàn Cảng' },
  // { id: 'berth', label: 'Cầu Tàu' },
  // { id: 'yard', label: 'Bãi Container' },
  // { id: 'gate', label: 'Cổng' },
  // { id: 'report', label: 'Báo Cáo Ca' },
]

// ─── Color mode ───────────────────────────────────────────────────────────────
const colorMode = ref<ColorMode>('cargo-empty')
const colorModes: { id: ColorMode; label: string; color: string; implemented: boolean }[] = [
  { id: 'cargo-empty', label: 'Hàng / Rỗng', color: '#4ade80', implemented: true },
  { id: 'by-line', label: 'Theo Hãng Tàu', color: '#f59e0b', implemented: false },
  { id: 'size', label: 'Kích cỡ container', color: '#60a5fa', implemented: false },
  { id: 'vessel', label: 'Tàu / Chuyến', color: '#a78bfa', implemented: false },
  { id: 'direction', label: 'Nhập / Xuất / Lưu vỏ', color: '#fb923c', implemented: false },
]

// ─── Clock ────────────────────────────────────────────────────────────────────
const now = ref(new Date())
let clockTimer: ReturnType<typeof setInterval>
const timeStr = computed(() =>
  now.value.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
)

// ─── Map ──────────────────────────────────────────────────────────────────────
const mapContainer = ref<HTMLElement | null>(null)
const initialBearing = 323.6
const bearing = ref(initialBearing)
let map: LeafletMap | null = null

// ─── Bearing input (decoupled từ bearing để tránh re-render đè khi đang gõ) ──
const bearingInputStr = ref(initialBearing.toFixed(2))
let bearingInputFocused = false

watch(bearing, (val) => {
  if (!bearingInputFocused) bearingInputStr.value = val.toFixed(2)
})

function onBearingFocus() { bearingInputFocused = true }
function onBearingBlur() {
  bearingInputFocused = false
  const val = parseFloat(bearingInputStr.value)
  if (!isNaN(val)) setBearing(Math.max(0, Math.min(360, val)))
  bearingInputStr.value = bearing.value.toFixed(2)
}
function onBearingEnter(e: KeyboardEvent) {
  (e.target as HTMLInputElement).blur()
}

// ─── Status bar state ─────────────────────────────────────────────────────────
const mapCoords = reactive({ lat: 0, lng: 0 })
const mapZoom = ref(0)
const mapMinZoom = ref(0)
const mapMaxZoom = ref(19)
const atMinZoom = computed(() => mapZoom.value <= mapMinZoom.value)
const atMaxZoom = computed(() => mapZoom.value >= mapMaxZoom.value)

// ─── Layer definitions (existing + future placeholders) ───────────────────────
const layerDefs = [
  { key: 'yard_boundary', label: 'Ranh giới bãi', available: true },
  { key: 'yard_area', label: 'Vùng bãi / Cầu tàu', available: true },
  { key: 'yard_block', label: 'Block container', available: true },
  { key: 'berth', label: 'Tàu cập bến', available: true },
  { key: 'landmarks', label: 'Thiết bị RTG/RS', available: true },
  { key: 'tractor', label: 'Xe kéo (tractor)', available: false },
  { key: 'gate_lane', label: 'Cổng (Lane)', available: false },
  { key: 'yard_road', label: 'Đường nội bộ', available: true },
  { key: 'building', label: 'Tòa nhà', available: true },
]

const layerVisibility = reactive<Record<string, boolean>>({
  yard_boundary: true,
  yard_block: true,
  yard_area: true,
  yard_road: true,
  building: true,
  landmarks: true,
  berth: false,
})
const featureCounts = reactive<Record<string, number>>({})
const layerInstances = new Map<string, L.GeoJSON>()

const totalFeatures = computed(() => Object.values(featureCounts).reduce((a, b) => a + b, 0))

// ─── Highlight ────────────────────────────────────────────────────────────────
let highlightedLayer: L.Path | null = null
let highlightedParent: L.GeoJSON | null = null

function highlightFeature(parent: L.GeoJSON, layer: L.Path) {
  resetHighlight()
  layer.setStyle({ color: '#00e5ff', weight: 4 })
  layer.bringToFront()
  highlightedLayer = layer
  highlightedParent = parent
}

function resetHighlight() {
  if (highlightedLayer && highlightedParent) highlightedParent.resetStyle(highlightedLayer)
  highlightedLayer = null
  highlightedParent = null
}

// ─── Layer loading ────────────────────────────────────────────────────────────
async function loadGeoJSON(url: string): Promise<GeoJSON.FeatureCollection> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to load ${url}: HTTP ${res.status}`)
  return res.json()
}

async function loadLayer(name: string, url: string, options: L.GeoJSONOptions) {
  try {
    const data = await loadGeoJSON(url)
    featureCounts[name] = data.features.length
    const origOnEach = options.onEachFeature
    options.onEachFeature = (feature, layer) => {
      origOnEach?.(feature, layer)
      layer.on('click', (e: L.LeafletMouseEvent) => {
        if (mockMode.value) { e.originalEvent.stopPropagation(); mockClickHandler(e); return }
        if ('setStyle' in layer) highlightFeature(geoLayer, layer as L.Path)
      })
      layer.on('popupclose', () => resetHighlight())
    }
    const geoLayer = L.geoJSON(data, options)
    layerInstances.set(name, geoLayer)
    if (map && layerVisibility[name]) geoLayer.addTo(map)
  } catch (err) {
    console.error(`Error loading ${name}:`, err)
  }
}

function toggleLayer(key: string) {
  if (!(key in layerVisibility)) return
  layerVisibility[key] = !layerVisibility[key]
  const layer = layerInstances.get(key)
  if (!layer || !map) return
  layerVisibility[key] ? layer.addTo(map) : map.removeLayer(layer)
}

// ─── Map zoom helpers (avoid template type-narrowing issue with `let map`) ────
function zoomIn() { map?.zoomIn() }
function zoomOut() { map?.zoomOut() }

// ─── Rotation ─────────────────────────────────────────────────────────────────
function updateCraneRotations(delta: number) {
  document.querySelectorAll<HTMLElement>('.crane-icon').forEach(el => {
    const cur = parseFloat(el.getAttribute('data-rot') ?? '0')
    const next = cur + delta
    el.setAttribute('data-rot', String(next))
    el.style.transform = `rotate(${next}deg)`
  })
}

function setBearing(deg: number) {
  const prev = bearing.value
  bearing.value = (deg + 360) % 360
  map?.setBearing(bearing.value)
  let delta = bearing.value - prev
  if (delta > 180) delta -= 360
  if (delta < -180) delta += 360
  updateCraneRotations(delta)
}

function rotateCW() { setBearing(bearing.value + 5) }
function rotateCCW() { setBearing(bearing.value - 5) }
function resetBearing() {
  setBearing(initialBearing)
  document.querySelectorAll<HTMLElement>('.crane-icon').forEach(el => {
    el.setAttribute('data-rot', '0')
    el.style.transform = 'rotate(0deg)'
  })
}

// ─── Slot grid ────────────────────────────────────────────────────────────────
const { grids: slotGrids, isLoading: slotLoading, error: slotError, fetchAll: fetchSlots } = useSlotGrid()
const slotLayerVisible = ref(false)
let slotGridLayer: SlotGridLayer | null = null

// ─── Container stream (SSE) ───────────────────────────────────────────────────
const {
  blockMap, streamStatus, totalContainers,
  connect: connectContainerStream,
  disconnect: disconnectContainerStream,
} = useContainerStream()

watch(blockMap, (newMap) => {
  if (slotLayerVisible.value && slotGridLayer) slotGridLayer.setContainers(newMap)
})

watch(colorMode, (mode) => {
  slotGridLayer?.setColorMode(mode)
})

// ─── Container search ─────────────────────────────────────────────────────────
const {
  query: searchQuery, results: searchResults,
  isSearching, searchError, debouncedSearch, clearSearch,
} = useContainerSearch()

function onSearchResultClick(result: { cBlock: string | null; bayIdx: number | null; rowIdx: number | null }) {
  if (!result.cBlock || result.bayIdx == null || result.rowIdx == null) return
  slotGridLayer?.setHighlight(result.cBlock, result.bayIdx, result.rowIdx)
  const blockLayer = layerInstances.get('yard_block')
  const grid = slotGrids.value.find(g => g.blockId === result.cBlock)
  if (!blockLayer || !grid || !map) return
  blockLayer.getLayers().forEach(l => {
    const f = (l as L.GeoJSON).feature as GeoJSON.Feature
    if (f?.properties?.block_id !== result.cBlock) return
    const geom = f.geometry as GeoJSON.MultiPolygon
    const ring = geom.coordinates[0]?.[0]
    if (!ring || ring.length < 4) return
    const [p0, p1, , p3] = ring
    if (!p0 || !p1 || !p3 || result.bayIdx == null || result.rowIdx == null) return
    const tb = (result.bayIdx - 0.5) / grid.maxBay
    const tr = (result.rowIdx - 0.5) / grid.maxRow
    const [lng0, lat0] = p0 as [number, number]
    const [lng1, lat1] = p1 as [number, number]
    const [lng3, lat3] = p3 as [number, number]
    const lng = lng0 + tb * (lng1 - lng0) + tr * (lng3 - lng0)
    const lat = lat0 + tb * (lat1 - lat0) + tr * (lat3 - lat0)
    map!.flyTo([lat, lng], map!.getMaxZoom(), { duration: 1 })
  })
}

function getYardBlockFeatures(): GeoJSON.Feature[] {
  const blockLayer = layerInstances.get('yard_block')
  if (!blockLayer) return []
  return blockLayer.getLayers().map(l => (l as L.GeoJSON).feature as GeoJSON.Feature)
}

function getYardAreaFeatures(): GeoJSON.Feature[] {
  const areaLayer = layerInstances.get('yard_area')
  if (!areaLayer) return []
  return areaLayer.getLayers().map(l => (l as L.GeoJSON).feature as GeoJSON.Feature)
}

async function toggleSlotLayer() {
  slotLayerVisible.value = !slotLayerVisible.value
  if (!map) return
  if (slotLayerVisible.value) {
    if (!slotGridLayer) slotGridLayer = new SlotGridLayer()
    if (slotGrids.value.length === 0) await fetchSlots()
    slotGridLayer.setColorMode(colorMode.value)
    slotGridLayer.setBlocks(getYardBlockFeatures(), slotGrids.value)
    slotGridLayer.setContainers(blockMap.value)
    slotGridLayer.addTo(map)
  } else {
    slotGridLayer?.remove()
  }
}

async function refreshSlotLayer() {
  await fetchSlots()
  slotGridLayer?.setBlocks(getYardBlockFeatures(), slotGrids.value)
}

// ─── Vehicle tracking ─────────────────────────────────────────────────────────
const {
  vehicles, registeredVehicles, selectedVehicleId,
  visible: vehicleVisible,
  start: startVehicleTracking,
  refresh: refreshVehicles,
  selectVehicle, clearTrack,
  toggleVisibility: toggleVehicleLayer,
} = useVehicleTracking(() => map, () => bearing.value)

// ─── Mock GPS ─────────────────────────────────────────────────────────────────
const {
  mockMode, mockVehicleId, mockSpeed,
  pendingPoint, points: mockPoints, saving: mockSaving,
  toggleMockMode, mockClickHandler,
  addPendingPoint, cancelPending,
  saveAll: saveMockPoints, exportJson: exportMockJson,
  clearPendingList: clearMockList, cleanup: cleanupMock,
} = useMockGps(() => map, {
  onSaved: refreshVehicles,
  onMapClick: resetHighlight,
  getVehiclePosition: (assetId) => {
    const v = vehicles.value.find(v => v.assetId === assetId)
    return v ? { lat: v.lat, lng: v.lng } : null
  },
})

watch(registeredVehicles, (list) => {
  if (list.length > 0 && !mockVehicleId.value)
    mockVehicleId.value = list[0]?.vehicleId ?? ''
}, { immediate: true })

watch(mockMode, active => {
  if (mapContainer.value) mapContainer.value.style.cursor = active ? 'crosshair' : ''
})

// ─── Selection state ──────────────────────────────────────────────────────────
const selectedObject = ref<SelectedObject | null>(null)

// ─── Vessel layer ─────────────────────────────────────────────────────────────
const {
  setBerthFeatures, start: startVesselLayer, stop: stopVesselLayer,
  setFilter: setVesselFilter,
} = useVesselLayer(
  () => map,
  (vessel) => {
    selectedObject.value = { type: 'vessel', data: vessel }
  })

const vesselFilter = reactive<VesselFilter>({ year: new Date().getFullYear().toString(), shipId: '', shipVoy: '' })

function applyVesselFilter() {
  if (layerVisibility.berth) setVesselFilter({ ...vesselFilter })
}

watch(() => layerVisibility.berth, (active) => {
  if (active) {
    setBerthFeatures(getYardAreaFeatures())
    startVesselLayer({ ...vesselFilter })
  } else {
    stopVesselLayer()
  }
})

// Dev tools panel collapse state
const devToolsOpen = ref(false)

// ─── Lifecycle ────────────────────────────────────────────────────────────────
onMounted(async () => {
  clockTimer = setInterval(() => { now.value = new Date() }, 1000)
  if (!mapContainer.value) return

  map = L.map(mapContainer.value, {
    rotate: true, bearing: 0, touchRotate: true, rotateControl: false,
  })
  map.on('click', () => resetHighlight())
  map.on('mousemove', (e) => { mapCoords.lat = e.latlng.lat; mapCoords.lng = e.latlng.lng })
  map.on('zoomend', () => { mapZoom.value = map?.getZoom() ?? 0 })

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map)

  const base = '/geojson/dvp'
  await loadLayer('yard_boundary', `${base}/yard_boundary.geojson`, { style: styleBoundary, interactive: false })
  await loadLayer('yard_area', `${base}/yard_area.geojson`, { style: styleArea, onEachFeature: (f, l) => l.bindPopup(popupArea(f.properties ?? {})) })
  setBerthFeatures(getYardAreaFeatures())
  await loadLayer('yard_road', `${base}/yard_road.geojson`, { style: styleRoad, renderer: L.canvas({ tolerance: 10 }), onEachFeature: (f, l) => l.bindPopup(popupRoad(f.properties ?? {})) } as L.GeoJSONOptions)
  await loadLayer('building', `${base}/building.geojson`, { style: styleBuilding, onEachFeature: (f, l) => l.bindPopup(popupBuilding(f.properties ?? {})) })
  await loadLayer('yard_block', `${base}/yard_block.geojson`, {
    style: styleBlock,
    onEachFeature: (f, l) => {
      l.bindPopup(popupBlock(f.properties ?? {}))
      const blockId = f.properties?.block_id ?? ''
      if (blockId) l.bindTooltip(blockId, { permanent: true, direction: 'center', className: 'block-label' })
    },
  })

  try {
    const landmarksRaw = await loadGeoJSON(`${base}/landmarks.geojson`)
    featureCounts['landmarks'] = landmarksRaw.features.length
    const regularData: GeoJSON.FeatureCollection = {
      ...landmarksRaw,
      features: landmarksRaw.features.filter(f => f.properties?.landmark_type !== 'rtg-crane'),
    }
    let geoLayer!: L.GeoJSON
    geoLayer = L.geoJSON(regularData, {
      pointToLayer(feature, latlng) {
        const lmType = feature.properties?.landmark_type ?? 'label'
        if (CRANE_TYPES.has(lmType)) return L.marker(latlng, { icon: makeCraneIcon(lmType, 0) })
        return L.marker(latlng, { icon: makeLandmarkIcon(lmType) })
      },
      onEachFeature(feature, layer) {
        layer.bindPopup(popupLandmark(feature.properties ?? {}))
        layer.on('click', (e: L.LeafletMouseEvent) => {
          if (mockMode.value) { e.originalEvent.stopPropagation(); mockClickHandler(e); return }
          if ('setStyle' in layer) highlightFeature(geoLayer, layer as L.Path)
        })
        layer.on('popupclose', () => resetHighlight())
      },
    })
    const rtgPositions = landmarksRaw.features
      .filter(f => f.properties?.landmark_type === 'rtg-crane' && f.geometry.type === 'Point')
      .map(f => {
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates as [number, number]
        return L.latLng(lat, lng)
      })
    const rtgLayer = new RtgCraneLayer()
    rtgLayer.setData(rtgPositions, getYardBlockFeatures())
    const landmarkGroup = L.layerGroup([geoLayer, rtgLayer])
    layerInstances.set('landmarks', landmarkGroup as unknown as L.GeoJSON)
    if (map && layerVisibility['landmarks']) landmarkGroup.addTo(map)
  } catch (err) {
    console.error('Error loading landmarks:', err)
  }

  const boundaryLayer = layerInstances.get('yard_boundary')
  if (boundaryLayer && map) {
    const bounds = boundaryLayer.getBounds()
    map.fitBounds(bounds, { padding: [20, 20] })
    const minZ = map.getBoundsZoom(bounds, false, L.point(20, 20)) + 1
    map.setMinZoom(minZ)
    map.setMaxBounds(bounds.pad(0.3))
    mapZoom.value = map.getZoom()
    mapMinZoom.value = minZ
    mapMaxZoom.value = map.getMaxZoom()
  }

  map.setBearing(initialBearing)
  startVehicleTracking()
  connectContainerStream()
})

onUnmounted(() => {
  clearInterval(clockTimer)
  cleanupMock()
  stopVesselLayer()
  disconnectContainerStream()
  slotGridLayer?.remove()
  slotGridLayer = null
  layerInstances.forEach(layer => layer.off())
  layerInstances.clear()
  if (map) { map.off(); map.remove(); map = null }
})
</script>

<template>
  <div class="dvp-monitor" :class="{ 'mock-mode': mockMode }">

    <!-- ════ TOP NAVBAR ═══════════════════════════════════════════════════════ -->
    <header class="dvp-topbar">
      <div class="dvp-logo">
        <span class="dvp-logo-badge">PL</span>
        <span class="dvp-logo-name">PortLogics</span>
      </div>

      <nav class="dvp-tabs">
        <button v-for="tab in tabs" :key="tab.id" class="dvp-tab" :class="{ 'dvp-tab--active': activeTab === tab.id }"
          @click="activeTab = tab.id">
          {{ tab.label }}
        </button>
      </nav>

      <div class="dvp-live">
        <span class="dvp-live-dot"></span>
        <span class="dvp-live-label">LIVE</span>
        <span class="dvp-live-time">{{ timeStr }}</span>
        <span class="dvp-live-shift">CA 2 · 13:00–21:00</span>
      </div>
    </header>

    <!-- ════ MAIN CONTENT ════════════════════════════════════════════════════ -->
    <div class="dvp-main">

      <!-- ── LEFT SIDEBAR ───────────────────────────────────────────────────── -->
      <aside class="dvp-sidebar dvp-sidebar--left">

        <!-- KPI: Tàu đang cập -->
        <div class="dvp-kpi">
          <div class="dvp-kpi-label">TÀU ĐANG CẬP</div>
          <div class="dvp-kpi-value">–</div>
          <div class="dvp-kpi-sub">Dữ liệu chưa kết nối</div>
        </div>

        <!-- KPI: Tồn bãi -->
        <div class="dvp-kpi">
          <div class="dvp-kpi-label">TỒN BÃI</div>
          <div class="dvp-kpi-value dvp-kpi-value--green">
            {{ totalContainers > 0 ? totalContainers.toLocaleString() : '–' }}
          </div>
          <div class="dvp-kpi-sub">
            <span class="dvp-stream-dot"
              :class="streamStatus === 'connected' ? 'dvp-stream-dot--ok' : streamStatus === 'error' ? 'dvp-stream-dot--err' : 'dvp-stream-dot--idle'">
            </span>
            <span :class="streamStatus === 'connected' ? 'text-emerald-400' : 'text-slate-500'">
              {{ streamStatus === 'connected' ? 'Stream OK' : streamStatus === 'error' ? 'Lỗi stream' : 'Chờ kết nối' }}
            </span>
          </div>
        </div>

        <!-- KPI: Xe qua cổng -->
        <div class="dvp-kpi">
          <div class="dvp-kpi-label">XE QUA CỔNG</div>
          <div class="dvp-kpi-value dvp-kpi-value--amber">–</div>
          <div class="dvp-kpi-sub">Dữ liệu chưa kết nối</div>
        </div>

        <!-- LỚP HIỂN THỊ -->
        <div class="dvp-section">
          <div class="dvp-section-title">LỚP HIỂN THỊ</div>
          <div class="dvp-layer-list">
            <label v-for="def in layerDefs" :key="def.key" class="dvp-layer-row"
              :class="{ 'dvp-layer-row--disabled': !def.available }">
              <input type="checkbox" :checked="def.available && layerVisibility[def.key]" :disabled="!def.available"
                @change="toggleLayer(def.key)" class="dvp-checkbox" />
              <span class="dvp-layer-label">{{ def.label }}</span>
              <span class="dvp-layer-count">
                {{ def.available && featureCounts[def.key] ? featureCounts[def.key] : '—' }}
              </span>
            </label>

            <!-- Slot grid as extra layer -->
            <label class="dvp-layer-row">
              <input type="checkbox" :checked="slotLayerVisible" :disabled="slotLoading" @change="toggleSlotLayer"
                class="dvp-checkbox" />
              <span class="dvp-layer-label">Lưới slot bãi</span>
              <span class="dvp-layer-count">{{ slotGrids.length ? `${slotGrids.length}b` : '—' }}</span>
            </label>
          </div>

          <div v-if="slotLoading" class="dvp-hint">Đang tải slot…</div>
          <div v-if="slotError" class="dvp-hint dvp-hint--err">{{ slotError }}</div>

          <button v-if="slotLayerVisible" @click="refreshSlotLayer" :disabled="slotLoading"
            class="dvp-btn dvp-btn--sm dvp-btn--block mt-1.5">
            ↻ Làm mới lưới
          </button>
        </div>

        <!-- LỌC TÀU CẬP BẾN — hiển thị khi bật lớp berth -->
        <div v-if="layerVisibility.berth" class="dvp-section">
          <div class="dvp-section-title">LỌC TÀU CẬP BẾN</div>
          <div class="dvp-vessel-filter">
            <div class="dvp-filter-row">
              <label class="dvp-filter-label">Năm</label>
              <input v-model="vesselFilter.year" type="text" maxlength="4" placeholder="tất cả"
                class="dvp-input dvp-input--xs" @keydown.enter="applyVesselFilter" />
            </div>
            <div class="dvp-filter-row">
              <label class="dvp-filter-label">Mã tàu</label>
              <input v-model="vesselFilter.shipId" type="text" placeholder="tất cả" class="dvp-input dvp-input--xs"
                @keydown.enter="applyVesselFilter" />
            </div>
            <div class="dvp-filter-row">
              <label class="dvp-filter-label">Chuyến</label>
              <input v-model="vesselFilter.shipVoy" type="text" placeholder="STQZ" class="dvp-input dvp-input--xs"
                @keydown.enter="applyVesselFilter" />
            </div>
            <button @click="applyVesselFilter" class="dvp-btn dvp-btn--primary dvp-btn--block dvp-btn--sm mt-2">
              ↻ Áp dụng
            </button>
          </div>
        </div>

        <!-- CHẾ ĐỘ MÀU -->
        <div class="dvp-section">
          <div class="dvp-section-title">CHẾ ĐỘ MÀU</div>
          <div class="dvp-colormode-list">
            <button v-for="mode in colorModes" :key="mode.id" class="dvp-colormode-row" :class="{
              'dvp-colormode-row--active': colorMode === mode.id,
              'dvp-colormode-row--todo': !mode.implemented,
            }" @click="colorMode = mode.id">
              <span class="dvp-colormode-swatch" :style="{ background: mode.color }"></span>
              <span>{{ mode.label }}</span>
              <span v-if="!mode.implemented" class="dvp-colormode-todo">soon</span>
            </button>
          </div>
          <div v-if="!slotLayerVisible" class="dvp-hint mt-1">Bật "Lưới slot bãi" để xem màu</div>
        </div>

        <!-- TÌM CONTAINER -->
        <div class="dvp-section" :class="{ 'opacity-70': !slotLayerVisible }">
          <div class="dvp-section-title">TÌM CONTAINER</div>
          <div v-if="!slotLayerVisible" class="dvp-hint">Bật "Lưới slot bãi" để tìm</div>
          <div class="flex gap-1">
            <input v-model="searchQuery" type="text" placeholder="TCKU1234567, Bill, …" :disabled="!slotLayerVisible"
              @input="debouncedSearch" @keydown.enter="debouncedSearch" @keydown.escape="clearSearch"
              class="dvp-input flex-1 min-w-0" />
            <button v-if="searchQuery && slotLayerVisible" @click="clearSearch" class="dvp-btn dvp-btn--icon">✕</button>
          </div>
          <div v-if="isSearching" class="dvp-hint">Đang tìm…</div>
          <div v-if="searchError" class="dvp-hint dvp-hint--err">{{ searchError }}</div>
          <div v-if="searchResults.length" class="dvp-search-results">
            <button v-for="r in searchResults" :key="r.cntrNo" class="dvp-search-item" @click="onSearchResultClick(r)">
              <div class="font-mono font-bold text-slate-100 text-xs">{{ r.cntrNo }}</div>
              <div class="text-slate-400 text-xs mt-0.5">
                {{ r.cBlock }} · Bay {{ r.bayIdx }} Row {{ r.rowIdx }}
                <span class="ml-1" :class="r.status === 'F' ? 'text-amber-400' : 'text-blue-400'">
                  {{ r.status === 'F' ? 'Hàng' : 'Vỏ' }}
                </span>
              </div>
            </button>
          </div>
          <div v-else-if="slotLayerVisible && searchQuery.length >= 2 && !isSearching && !searchError" class="dvp-hint">
            Không tìm thấy</div>
        </div>

        <!-- DEV TOOLS (collapsible) -->
        <div class="dvp-section">
          <button class="dvp-section-title dvp-devtools-toggle" @click="devToolsOpen = !devToolsOpen">
            <span>DEV TOOLS</span>
            <span>{{ devToolsOpen ? '▲' : '▼' }}</span>
          </button>

          <template v-if="devToolsOpen">
            <!-- Vehicle layer -->
            <label class="dvp-layer-row mt-1">
              <input type="checkbox" :checked="vehicleVisible" @change="toggleVehicleLayer" class="dvp-checkbox" />
              <span class="dvp-layer-label">Xe GPS</span>
              <span class="dvp-layer-count">{{ vehicles.length }}</span>
            </label>

            <!-- Vehicle buttons -->
            <div v-if="vehicles.length" class="flex flex-wrap gap-1 mt-1">
              <button v-for="v in vehicles" :key="v.assetId" @click="selectVehicle(v.assetId)" class="dvp-vehicle-btn"
                :class="{ 'dvp-vehicle-btn--active': v.assetId === selectedVehicleId }">
                {{ v.assetId }}
              </button>
              <button v-if="selectedVehicleId" @click="clearTrack" class="dvp-vehicle-btn">✕ track</button>
            </div>

            <!-- Mock GPS -->
            <div class="mt-2.5 border-t border-slate-800 pt-2">
              <div class="dvp-hint mb-1.5">Mock GPS</div>
              <select v-model="mockVehicleId" class="dvp-input w-full mb-1">
                <option v-for="v in registeredVehicles" :key="v.vehicleId" :value="v.vehicleId">
                  {{ v.name }}
                </option>
              </select>
              <input type="number" v-model.number="mockSpeed" min="0" max="60" placeholder="Tốc độ (km/h)"
                class="dvp-input w-full mb-1.5" />
              <button @click="toggleMockMode" class="dvp-btn dvp-btn--block"
                :class="mockMode ? 'dvp-btn--danger' : 'dvp-btn--default'">
                {{ mockMode ? '⏹ Dừng chấm' : '✚ Chấm điểm GPS' }}
              </button>
              <p v-if="mockMode && !pendingPoint" class="dvp-hint text-center mt-1">
                Click trên bản đồ để chọn tọa độ
              </p>
              <div v-if="pendingPoint" class="dvp-pending-point">
                <div class="text-xs font-semibold text-amber-300 mb-1">Xác nhận điểm?</div>
                <div class="text-xs font-mono text-slate-300">
                  {{ pendingPoint.lat.toFixed(6) }}, {{ pendingPoint.lng.toFixed(6) }}
                </div>
                <div class="flex gap-1 mt-1.5">
                  <button @click="addPendingPoint" class="dvp-btn dvp-btn--success flex-1 text-xs">✓ Thêm</button>
                  <button @click="cancelPending" class="dvp-btn dvp-btn--default flex-1 text-xs">✕ Hủy</button>
                </div>
              </div>
              <template v-if="mockPoints.length > 0">
                <div class="dvp-hint mt-1.5">{{ mockPoints.length }} điểm chờ lưu</div>
                <div class="flex gap-1 mt-1">
                  <button @click="saveMockPoints" :disabled="mockSaving"
                    class="dvp-btn dvp-btn--primary flex-1 text-xs">
                    {{ mockSaving ? '…' : '↑ Lưu' }}
                  </button>
                  <button @click="exportMockJson" class="dvp-btn dvp-btn--default flex-1 text-xs">↓ JSON</button>
                </div>
                <button @click="clearMockList" class="dvp-btn dvp-btn--danger dvp-btn--block dvp-btn--sm mt-1">
                  ✕ Xóa danh sách
                </button>
              </template>
            </div>

            <!-- Rotation -->
            <!-- <div class="mt-2.5 border-t border-slate-800 pt-2">
              <div class="dvp-hint mb-1.5">Xoay bản đồ</div>
              <div class="flex items-center gap-1">
                <button @click="rotateCCW" class="dvp-btn dvp-btn--icon" title="Xoay trái">↺</button>
                <input type="range" min="0" max="360" step="1" :value="bearing"
                  @input="setBearing(+($event.target as HTMLInputElement).value)" class="flex-1 accent-blue-500 h-1" />
                <button @click="rotateCW" class="dvp-btn dvp-btn--icon" title="Xoay phải">↻</button>
                <span class="text-xs font-mono text-slate-300 w-9 text-right shrink-0">{{ bearing }}°</span>
              </div>
              <button v-if="bearing !== initialBearing" @click="resetBearing"
                class="dvp-btn dvp-btn--sm dvp-btn--block mt-1">
                Reset xoay
              </button>
            </div> -->
          </template>
        </div>

      </aside>

      <!-- ── MAP AREA ────────────────────────────────────────────────────────── -->
      <div class="dvp-map-area">
        <div ref="mapContainer" class="w-full h-full"></div>

        <!-- Floating map controls (top-right overlay) -->
        <div class="dvp-map-controls">
          <button @click="zoomIn()" class="dvp-map-btn" :class="{ 'dvp-map-btn--limit': atMaxZoom }"
            :disabled="atMaxZoom" title="Phóng to">+</button>
          <button @click="zoomOut()" class="dvp-map-btn" :class="{ 'dvp-map-btn--limit': atMinZoom }"
            :disabled="atMinZoom" title="Thu nhỏ">−</button>
          <div style="height:6px"></div>
          <button @click="rotateCCW" class="dvp-map-btn" title="Xoay trái">↺</button>
          <button @click="rotateCW" class="dvp-map-btn" title="Xoay phải">↻</button>
          <button @click="resetBearing" class="dvp-map-btn"
            :class="{ 'dvp-map-btn--active': bearing !== initialBearing }"
            :title="`Reset bearing (${Math.round(bearing)}°)`">◎</button>
        </div>
      </div>

      <!-- ── RIGHT SIDEBAR ───────────────────────────────────────────────────── -->
      <aside class="dvp-sidebar dvp-sidebar--right">

        <!-- CHI TIẾT ĐỐI TƯỢNG -->
        <div class="dvp-section">
          <component v-if="selectedObject" :is="panelMap[selectedObject.type]" :data="selectedObject.data"
            @close="selectedObject = null" />
          <template v-else>
            <div class="dvp-section-title">CHI TIẾT ĐỐI TƯỢNG</div>
            <div class="dvp-placeholder">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
                class="mb-2 opacity-30">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
              <span>Chọn một đối tượng trên sơ đồ</span>
            </div>
          </template>
        </div>

        <!-- THIẾT BỊ KHU VỰC -->
        <div class="dvp-section">
          <div class="dvp-section-title">THIẾT BỊ KHU VỰC</div>
          <div class="dvp-placeholder">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
              class="mb-2 opacity-30">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <span>Chọn Block / Berth để xem thiết bị</span>
          </div>
          <!--
            TODO (B-6, B-7, Y-7, Y-8, Y-9):
            List QC / RTG / RS / Tractor theo khu vực:
            - dvp-equip-item: icon + tên + vị trí + badge trạng thái
            - Badge states: HOẠT ĐỘNG (green) / DI CHUYỂN (blue) / CHỜ VIỆC (amber) / BẢO TRÌ (red)
          -->
        </div>

        <!-- SẢN LƯỢNG CA -->
        <div class="dvp-section">
          <div class="dvp-section-title">SẢN LƯỢNG CA</div>
          <div class="dvp-placeholder">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
              class="mb-2 opacity-30">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <span>Dữ liệu chưa kết nối</span>
          </div>
          <!--
            TODO (API TOS productivity):
            - Nâng bãi / Hạ bãi / Đảo chuyển (moves)
            - Xuất cẩu / Nhập cẩu (TEU)
          -->
        </div>

        <!-- Legend slot grid (hiện khi slot layer bật) -->
        <div v-if="slotLayerVisible" class="dvp-section">
          <div class="dvp-section-title">CHÚ GIẢI</div>
          <div class="dvp-legend">
            <div class="dvp-legend-item">
              <span class="dvp-legend-swatch" style="background:rgba(230,81,0,0.55)"></span>
              <span>Hàng</span>
            </div>
            <div class="dvp-legend-item">
              <span class="dvp-legend-swatch" style="background:rgba(66,133,244,0.45)"></span>
              <span>Vỏ</span>
            </div>
            <div class="dvp-legend-item">
              <span class="dvp-legend-swatch"
                style="background:rgba(0,229,255,0.6); border:1px solid rgba(0,229,255,0.8)"></span>
              <span>Tìm thấy</span>
            </div>
          </div>
        </div>

      </aside>
    </div>

    <!-- ════ STATUS BAR ═══════════════════════════════════════════════════════ -->
    <footer class="dvp-statusbar">
      <span>Cập nhật: {{ timeStr }}</span>
      <span class="dvp-sep">·</span>
      <span>Features: {{ totalFeatures }}</span>
      <span class="dvp-sep">·</span>
      <span>Zoom: {{ mapZoom }}</span>
      <span class="dvp-sep">·</span>
      <!-- Bearing: display + editable input -->
      <span class="dvp-status-bearing">
        <span class="dvp-status-bearing-label">Xoay</span>
        <input type="number" min="0" max="360" step="0.01" v-model="bearingInputStr" @focus="onBearingFocus"
          @blur="onBearingBlur" @keydown.enter="onBearingEnter" class="dvp-status-bearing-input" />
        <span>°</span>
        <button v-if="Math.abs(bearing - initialBearing) > 0.005" @click="resetBearing" class="dvp-status-bearing-reset"
          title="Reset bearing">↺</button>
      </span>
      <span class="dvp-sep">·</span>
      <span v-if="mapCoords.lat !== 0" class="font-mono">
        {{ mapCoords.lat.toFixed(4) }}°N {{ mapCoords.lng.toFixed(4) }}°E
      </span>
      <span v-else class="text-slate-600">Di chuột trên bản đồ để xem tọa độ</span>
      <span class="ml-auto text-slate-600">PortLogics v0.1</span>
    </footer>

  </div>
</template>

<style scoped>
/* ── Root layout ──────────────────────────────────────────────────────────── */
.dvp-monitor {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  background: #0a0f1a;
  color: #e2e8f0;
  font-size: 13px;
  overflow: hidden;
  font-family: system-ui, -apple-system, sans-serif;
}

/* ── Top bar ──────────────────────────────────────────────────────────────── */
.dvp-topbar {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 12px;
  gap: 10px;
  flex-shrink: 0;
  background: #111827;
  border-bottom: 1px solid #1e2d45;
}

.dvp-logo {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
  margin-right: 4px;
}

.dvp-logo-badge {
  background: #2563eb;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
}

.dvp-logo-name {
  font-size: 13px;
  font-weight: 700;
  color: #f1f5f9;
  white-space: nowrap;
}

.dvp-tabs {
  display: flex;
  align-items: stretch;
  height: 100%;
  gap: 2px;
}

.dvp-tab {
  padding: 0 13px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}

.dvp-tab:hover {
  color: #94a3b8;
}

.dvp-tab--active {
  color: #60a5fa;
  border-bottom-color: #3b82f6;
  background: rgba(59, 130, 246, 0.07);
}

.dvp-live {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.dvp-live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 6px #4ade80;
  animation: dvp-blink 2s ease-in-out infinite;
}

@keyframes dvp-blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.35;
  }
}

.dvp-live-label {
  font-size: 11px;
  font-weight: 700;
  color: #4ade80;
  letter-spacing: 1px;
}

.dvp-live-time {
  font-size: 13px;
  font-weight: 700;
  font-family: monospace;
  color: #f1f5f9;
}

.dvp-live-shift {
  font-size: 11px;
  color: #64748b;
}

/* ── Main container ───────────────────────────────────────────────────────── */
.dvp-main {
  flex: 1;
  display: flex;
  min-height: 0;
  overflow: hidden;
}

/* ── Sidebars ─────────────────────────────────────────────────────────────── */
.dvp-sidebar {
  width: 200px;
  flex-shrink: 0;
  background: #0f1729;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
}

.dvp-sidebar--left {
  border-right: 1px solid #1e2d45;
}

.dvp-sidebar--right {
  border-left: 1px solid #1e2d45;
  width: 220px;
}

.dvp-sidebar::-webkit-scrollbar {
  width: 4px;
}

.dvp-sidebar::-webkit-scrollbar-track {
  background: transparent;
}

.dvp-sidebar::-webkit-scrollbar-thumb {
  background: #2a3f5f;
  border-radius: 2px;
}

.dvp-sidebar::-webkit-scrollbar-thumb:hover {
  background: #3b5480;
}

/* ── KPI cards ────────────────────────────────────────────────────────────── */
.dvp-kpi {
  padding: 10px 12px 9px;
  border-bottom: 1px solid #151f30;
}

.dvp-kpi-label {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.9px;
  color: #3d5270;
  text-transform: uppercase;
  margin-bottom: 3px;
}

.dvp-kpi-value {
  font-size: 28px;
  font-weight: 800;
  line-height: 1;
  color: #f1f5f9;
}

.dvp-kpi-value--green {
  color: #4ade80;
}

.dvp-kpi-value--amber {
  color: #fbbf24;
}

.dvp-kpi-sub {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #475569;
  margin-top: 3px;
}

.dvp-stream-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dvp-stream-dot--ok {
  background: #4ade80;
}

.dvp-stream-dot--err {
  background: #f87171;
}

.dvp-stream-dot--idle {
  background: #475569;
}

/* ── Sections ─────────────────────────────────────────────────────────────── */
.dvp-section {
  padding: 10px 12px;
  border-bottom: 1px solid #151f30;
}

.dvp-section-title {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.9px;
  color: #3d5270;
  text-transform: uppercase;
  margin-bottom: 8px;
  display: block;
}

.dvp-devtools-toggle {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #3d5270;
}

.dvp-devtools-toggle:hover {
  color: #64748b;
}

/* ── Layer list ───────────────────────────────────────────────────────────── */
.dvp-layer-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dvp-layer-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  cursor: pointer;
  color: #94a3b8;
  user-select: none;
  transition: color 0.1s;
}

.dvp-layer-row:hover:not(.dvp-layer-row--disabled) {
  color: #cbd5e1;
}

.dvp-layer-row--disabled {
  opacity: 0.38;
  cursor: default;
}

.dvp-checkbox {
  accent-color: #3b82f6;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  cursor: pointer;
}

.dvp-layer-label {
  flex: 1;
  font-size: 12px;
}

.dvp-layer-count {
  font-size: 10px;
  color: #374151;
  min-width: 18px;
  text-align: right;
}

/* ── Color mode ───────────────────────────────────────────────────────────── */
.dvp-colormode-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dvp-colormode-row {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 4px 6px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 12px;
  color: #475569;
  background: transparent;
  border: 1px solid transparent;
  text-align: left;
  width: 100%;
  transition: all 0.12s;
}

.dvp-colormode-row:hover {
  background: #162035;
  color: #94a3b8;
}

.dvp-colormode-row--active {
  background: #162035;
  color: #e2e8f0;
  border-color: #2a3f5f;
}

.dvp-colormode-swatch {
  width: 13px;
  height: 13px;
  border-radius: 3px;
  flex-shrink: 0;
}

.dvp-colormode-row--todo {
  opacity: 0.5;
}

.dvp-colormode-row--todo:hover {
  opacity: 0.7;
}

.dvp-colormode-todo {
  margin-left: auto;
  font-size: 9px;
  color: #4b5563;
  letter-spacing: 0.5px;
  background: #1e293b;
  padding: 1px 4px;
  border-radius: 3px;
}

/* ── Input ────────────────────────────────────────────────────────────────── */
.dvp-input {
  background: #162035;
  border: 1px solid #263548;
  border-radius: 5px;
  color: #e2e8f0;
  font-size: 12px;
  padding: 4px 8px;
  outline: none;
  transition: border-color 0.15s;
}

.dvp-input:focus {
  border-color: #3b82f6;
}

.dvp-input:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.dvp-input::placeholder {
  color: #374151;
}

.dvp-input--xs {
  flex: 1;
  padding: 3px 6px;
  font-size: 11px;
}

/* ── Vessel filter form ────────────────────────────────────────────────────── */
.dvp-vessel-filter {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dvp-filter-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dvp-filter-label {
  font-size: 10px;
  color: #64748b;
  width: 40px;
  flex-shrink: 0;
}

/* ── Search results ───────────────────────────────────────────────────────── */
.dvp-search-results {
  margin-top: 5px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 150px;
  overflow-y: auto;
}

.dvp-search-item {
  width: 100%;
  text-align: left;
  padding: 5px 7px;
  border-radius: 5px;
  border: 1px solid transparent;
  cursor: pointer;
  background: transparent;
  color: inherit;
  transition: all 0.1s;
}

.dvp-search-item:hover {
  background: #162035;
  border-color: #2a3f5f;
}

/* ── Buttons ──────────────────────────────────────────────────────────────── */
.dvp-btn {
  padding: 4px 10px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid #263548;
  background: #162035;
  color: #94a3b8;
  transition: all 0.12s;
  text-align: center;
}

.dvp-btn:hover:not(:disabled) {
  background: #1e2d45;
  color: #e2e8f0;
}

.dvp-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dvp-btn--sm {
  padding: 3px 8px;
  font-size: 11px;
}

.dvp-btn--icon {
  padding: 3px 7px;
}

.dvp-btn--block {
  width: 100%;
  display: block;
}

.dvp-btn--primary {
  background: #1d4ed8;
  border-color: #2563eb;
  color: #fff;
}

.dvp-btn--primary:hover:not(:disabled) {
  background: #2563eb;
}

.dvp-btn--danger {
  background: #450a0a;
  border-color: #7f1d1d;
  color: #fca5a5;
}

.dvp-btn--danger:hover:not(:disabled) {
  background: #7f1d1d;
}

.dvp-btn--success {
  background: #052e16;
  border-color: #14532d;
  color: #86efac;
}

.dvp-btn--success:hover:not(:disabled) {
  background: #14532d;
}

.dvp-btn--default {
  background: #1e293b;
  border-color: #334155;
  color: #94a3b8;
}

/* ── Hints ────────────────────────────────────────────────────────────────── */
.dvp-hint {
  font-size: 11px;
  color: #475569;
  margin-top: 3px;
}

.dvp-hint--err {
  color: #f87171;
}

/* ── Pending GPS point ────────────────────────────────────────────────────── */
.dvp-pending-point {
  margin-top: 6px;
  padding: 8px;
  background: #1a1500;
  border: 1px solid #78350f;
  border-radius: 6px;
}

/* ── Vehicle buttons ──────────────────────────────────────────────────────── */
.dvp-vehicle-btn {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 99px;
  border: 1px solid #334155;
  background: #1e293b;
  color: #64748b;
  cursor: pointer;
  transition: all 0.1s;
}

.dvp-vehicle-btn:hover {
  color: #94a3b8;
  background: #263548;
}

.dvp-vehicle-btn--active {
  background: #f97316;
  border-color: #ea580c;
  color: #fff;
}

/* ── Map area ─────────────────────────────────────────────────────────────── */
.dvp-map-area {
  flex: 1;
  position: relative;
  min-width: 0;
  overflow: hidden;
}

/* Floating controls overlay */
.dvp-map-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.dvp-map-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 41, 0.82);
  border: 1px solid #2a3f5f;
  border-radius: 6px;
  color: #64748b;
  font-size: 16px;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: all 0.12s;
}

.dvp-map-btn:hover {
  background: rgba(30, 45, 69, 0.92);
  color: #e2e8f0;
}

.dvp-map-btn--active {
  color: #60a5fa;
  border-color: #3b82f6;
}

.dvp-map-btn--limit {
  opacity: 0.3;
  cursor: not-allowed;
}

.dvp-map-btn:disabled {
  cursor: not-allowed;
}

/* ── Right sidebar placeholders ───────────────────────────────────────────── */
.dvp-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60px;
  color: #374151;
  font-size: 11px;
  text-align: center;
  gap: 2px;
}

/* ── Legend ───────────────────────────────────────────────────────────────── */
.dvp-legend {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dvp-legend-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #64748b;
}

.dvp-legend-swatch {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  flex-shrink: 0;
}

/* ── Status bar ───────────────────────────────────────────────────────────── */
.dvp-statusbar {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 24px;
  padding: 0 12px;
  flex-shrink: 0;
  background: #111827;
  border-top: 1px solid #1e2d45;
  font-size: 11px;
  color: #475569;
}

.dvp-sep {
  color: #263548;
}

/* ── Statusbar bearing control ────────────────────────────────────────────── */
.dvp-status-bearing {
  display: flex;
  align-items: center;
  gap: 3px;
}

.dvp-status-bearing-label {
  color: #374151;
}

.dvp-status-bearing-input {
  width: 56px;
  background: #162035;
  border: 1px solid #263548;
  border-radius: 3px;
  color: #94a3b8;
  font-size: 11px;
  font-family: monospace;
  padding: 1px 4px;
  outline: none;
  text-align: right;
  -moz-appearance: textfield;
}

.dvp-status-bearing-input::-webkit-inner-spin-button,
.dvp-status-bearing-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
}

.dvp-status-bearing-input:focus {
  border-color: #3b82f6;
  color: #e2e8f0;
}

.dvp-status-bearing-reset {
  background: transparent;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 12px;
  padding: 0 2px;
  line-height: 1;
}

.dvp-status-bearing-reset:hover {
  color: #60a5fa;
}

/* ── Mock mode cursor override ────────────────────────────────────────────── */
.mock-mode .leaflet-interactive {
  cursor: crosshair !important;
}
</style>

<!-- Global styles: Leaflet tooltips + vessel overlay (outside scoped) + ... -->
<style>
.dvp-vessel-overlay {
  cursor: pointer !important;
  transition: opacity 0.15s;
}

.dvp-vessel-overlay:hover {
  opacity: 1 !important;
  filter: drop-shadow(0 0 6px rgba(96, 165, 250, 0.7));
}

.block-label {
  background: none !important;
  border: none !important;
  box-shadow: none !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  color: #c62828 !important;
  text-shadow: 0 0 3px #fff, 0 0 3px #fff, 0 0 3px #fff;
  white-space: nowrap;
}

/* ── Detail panel (right sidebar) ───────────────────────────────────────── */
.dvp-close-btn {
  margin-left: auto;
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 11px;
  padding: 0 2px;
  line-height: 1;
}

.dvp-close-btn:hover {
  color: #f87171;
}

.dvp-detail-block {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding-top: 4px;
}

.dvp-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
}

.dvp-detail-key {
  color: #64748b;
  flex-shrink: 0;
  font-size: 11px;
}

.dvp-detail-val {
  color: #e2e8f0;
  text-align: right;
  word-break: break-all;
}
</style>
